import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowLeft } from 'lucide-react';
import { createProduttore } from '../api/produttori';
import type { ProduttoreCreatePayload } from '../api/produttori';
import { apiErrorMessage } from '../lib/apiError';
import { produttoreCreateSchema, type ProduttoreCreateData } from '../schemas/produttore';

// ── Stili ─────────────────────────────────────────────────────────────────────

const inputCls = 'w-full text-sm text-ink-dark border border-stone-border rounded px-2 py-1.5 focus:outline-none focus:border-sage focus:ring-1 focus:ring-sage/20 bg-white';

function FieldWrap({
  label,
  error,
  required,
  children,
}: {
  label: string;
  error?: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="flex gap-4 py-2 border-b border-stone-mid last:border-0 items-start">
      <label className="text-xs text-ink-soft min-w-[160px] pt-2 flex-shrink-0">
        {label}
        {required && <span className="text-accent-danger ml-0.5">*</span>}
      </label>
      <div className="flex-1">
        {children}
        {error && <p className="text-xs text-accent-danger mt-0.5">{error}</p>}
      </div>
    </div>
  );
}

// ── Pagina principale ─────────────────────────────────────────────────────────

export default function CreaProduttorePage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isSaving, setIsSaving] = useState(false);
  const [globalError, setGlobalError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<ProduttoreCreateData>({
    resolver: zodResolver(produttoreCreateSchema),
    defaultValues: {
      ragione_sociale: '',
      piva: '',
      codice_fiscale: '',
      email: '',
      telefono: '',
      indirizzo: '',
      citta: '',
      provincia: '',
      cap: '',
      note: '',
      tipo_soggetto: '',
    },
  });

  function buildPayload(data: ProduttoreCreateData): ProduttoreCreatePayload {
    return {
      ragione_sociale: data.ragione_sociale,
      piva: data.piva?.trim() || null,
      codice_fiscale: data.codice_fiscale?.trim() || null,
      email: data.email?.trim() || null,
      telefono: data.telefono?.trim() || null,
      indirizzo: data.indirizzo?.trim() || null,
      citta: data.citta?.trim() || null,
      provincia: data.provincia?.trim() || null,
      cap: data.cap?.trim() || null,
      note: data.note?.trim() || null,
      tipo_soggetto: (data.tipo_soggetto?.trim() as 'azienda' | 'privato' | 'ente_pubblico' | null) || null,
    };
  }

  const onSubmit = async (data: ProduttoreCreateData) => {
    setIsSaving(true);
    setGlobalError(null);
    try {
      const created = await createProduttore(buildPayload(data));
      navigate(`/produttori/${created.id}`);
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 409) {
        setError('piva', { message: t('anagrafiche.crea_produttore.piva_esistente') });
      } else {
        // T1 sul CF vale anche qui: il 422 ha un detail parlante (stringa o
        // array Pydantic) — `apiErrorMessage` lo estrae invece di scartarlo.
        setGlobalError(apiErrorMessage(err, t('anagrafiche.crea_produttore.errore_salvataggio')));
      }
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="p-8">
      <div className="max-w-3xl mx-auto">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm mb-6">
          <Link to="/produttori" className="text-sage hover:underline flex items-center gap-1">
            <ArrowLeft size={14} />
            {t('anagrafiche.produttori.titolo')}
          </Link>
          <span className="text-ink-faint">/</span>
          <span className="font-semibold text-ink-dark">{t('anagrafiche.crea_produttore.titolo')}</span>
        </div>

        <div className="bg-white border border-stone-mid rounded-xl overflow-hidden">
          <div className="px-6 py-4 border-b border-stone-mid bg-stone-bg">
            <h1 className="text-base font-semibold text-ink-dark">{t('anagrafiche.crea_produttore.titolo')}</h1>
            <p className="text-xs text-ink-soft mt-0.5">{t('anagrafiche.crea_produttore.sottotitolo')}</p>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="px-6 py-4">
            {globalError && (
              <div className="mb-4 px-3 py-2 bg-accent-danger-bg border border-[#FECACA] rounded-md text-sm text-accent-danger">
                {globalError}
              </div>
            )}

            <dl className="divide-y divide-stone-mid">
              <FieldWrap label={t('anagrafiche.campi.ragione_sociale')} required error={errors.ragione_sociale?.message}>
                <input className={inputCls} data-testid="input-produttore-ragione-sociale" {...register('ragione_sociale')} />
              </FieldWrap>
              <FieldWrap label={t('anagrafiche.campi.partita_iva')} error={errors.piva?.message}>
                <input className={inputCls} {...register('piva')} maxLength={11} placeholder={t('anagrafiche.crea_cliente.ph_piva')} />
              </FieldWrap>
              <FieldWrap label={t('anagrafiche.campi.codice_fiscale')} error={errors.codice_fiscale?.message}>
                <input className={inputCls} data-testid="input-produttore-cf" {...register('codice_fiscale')} maxLength={16} placeholder={t('anagrafiche.crea_cliente.ph_cf')} />
              </FieldWrap>
              <FieldWrap label={t('anagrafiche.campi.tipo_soggetto')} error={errors.tipo_soggetto?.message}>
                <select className={inputCls} {...register('tipo_soggetto')}>
                  <option value="">{t('anagrafiche.comune.seleziona')}</option>
                  <option value="azienda">{t('anagrafiche.tipo_soggetto.azienda')}</option>
                  <option value="privato">{t('anagrafiche.tipo_soggetto.privato')}</option>
                  <option value="ente_pubblico">{t('anagrafiche.tipo_soggetto.ente_pubblico')}</option>
                </select>
              </FieldWrap>
              <FieldWrap label={t('anagrafiche.campi.email')} error={errors.email?.message}>
                <input className={inputCls} type="email" {...register('email')} />
              </FieldWrap>
              <FieldWrap label={t('anagrafiche.campi.telefono')} error={errors.telefono?.message}>
                <input className={inputCls} {...register('telefono')} />
              </FieldWrap>
              <FieldWrap label={t('anagrafiche.campi.indirizzo')} error={errors.indirizzo?.message}>
                <input className={inputCls} {...register('indirizzo')} />
              </FieldWrap>
              <FieldWrap label={t('anagrafiche.campi.citta')} error={errors.citta?.message}>
                <input className={inputCls} {...register('citta')} />
              </FieldWrap>
              <FieldWrap label={t('anagrafiche.campi.provincia')} error={errors.provincia?.message}>
                <input className={inputCls} {...register('provincia')} maxLength={2} />
              </FieldWrap>
              <FieldWrap label={t('anagrafiche.campi.cap')} error={errors.cap?.message}>
                <input className={inputCls} {...register('cap')} maxLength={5} />
              </FieldWrap>
              <FieldWrap label={t('anagrafiche.campi.note')} error={errors.note?.message}>
                <textarea className={inputCls} rows={3} {...register('note')} />
              </FieldWrap>
            </dl>

            <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-stone-mid">
              <button
                type="button"
                onClick={() => navigate('/produttori')}
                className="px-4 py-2 text-sm text-ink-mid border border-stone-border rounded-lg hover:bg-stone-bg transition-colors"
              >
                {t('common.azioni.annulla')}
              </button>
              <button
                type="submit"
                disabled={isSaving}
                className="px-5 py-2 text-sm font-medium text-white bg-sage rounded-lg hover:bg-sage-dark transition-colors disabled:opacity-60"
              >
                {isSaving ? t('anagrafiche.crea_produttore.salvataggio') : t('anagrafiche.crea_produttore.crea')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
